import { Navigate, Outlet, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/shared/hooks/useAuthStore.ts';
import { useEffect, useRef, useState } from 'react';
import axios from 'axios';

/**
 * Guard que protege rotas admin.
 *
 * - Captura ?token= da URL (vindo do callback OAuth) e armazena no Zustand
 * - Se não há accessToken, tenta renovar via refresh cookie (httpOnly)
 * - Se nenhuma das duas funciona, redireciona para /login
 */
export function RequireAuth() {
  const { accessToken, setAccessToken } = useAuthStore();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const processed = useRef(false);
  const [checking, setChecking] = useState(!accessToken && !searchParams.get('token'));

  const tokenFromUrl = searchParams.get('token');

  // Capturar token da URL (callback OAuth)
  useEffect(() => {
    if (tokenFromUrl && !processed.current) {
      processed.current = true;
      setAccessToken(tokenFromUrl);
      navigate(window.location.pathname, { replace: true });
    }
  }, [tokenFromUrl, setAccessToken, navigate]);

  // Tentar renovar token via refresh cookie quando não há accessToken
  useEffect(() => {
    if (accessToken || tokenFromUrl) return;

    let cancelled = false;

    async function tryRefresh() {
      try {
        const { data } = await axios.post<{ accessToken: string }>(
          `${import.meta.env.VITE_API_BASE_URL || ''}/api/auth/refresh`,
          {},
          { withCredentials: true },
        );
        if (!cancelled) {
          setAccessToken(data.accessToken);
        }
      } catch {
        // Refresh falhou — sem sessão válida
      } finally {
        if (!cancelled) {
          setChecking(false);
        }
      }
    }

    tryRefresh();
    return () => { cancelled = true; };
  }, [accessToken, tokenFromUrl, setAccessToken]);

  // Aguardar processamento do token da URL
  if (tokenFromUrl && !accessToken) {
    return null;
  }

  // Aguardar tentativa de refresh
  if (checking) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-gray-950">
        <div className="text-sm text-gray-400">Verificando sessão...</div>
      </div>
    );
  }

  if (!accessToken) {
    return <Navigate to="/login" replace />;
  }

  return <Outlet />;
}
